import React, { useCallback, useContext, useEffect } from "react";
import { MotionConfig } from "framer-motion";
import { AppStateContext, WorkoutContext } from "./contexts/WorkoutContext";
import { useLiveSession } from "./contexts/SocialContext";
import ProgrammeView from "./views/ProgrammeView";
import SessionView from "./views/SessionView";
import WorkoutView from "./views/CoachView";
import SettingsView from "./views/SettingsView";
import ProfileView from "./views/ProfileView";
import SocialView from "./views/SocialView";
import ActivityView from "./views/ActivityView";
import ProgressView from "./views/ProgressView";
import { Tabs, TabsList, TabsTrigger } from "./components/ui/tabs";
import { useBodyPointerEventsGuard } from "./hooks/useBodyPointerEventsGuard";
import { useServiceWorkerUpdate } from "./hooks/useServiceWorkerUpdate";
import { useTargetSuggestionNotice } from "./hooks/useTargetSuggestionNotice";
import { useRareEffortNotice } from "./hooks/useRareEffortNotice";
import useTimerMetricsRelay from "./hooks/useTimerMetricsRelay";
import GettingStarted from "./components/setup/GettingStarted";
import HelpCentre from "./components/help/HelpCentre";
import Tour from "./components/help/Tour";
import { openHelp } from "./utils/helpBus";
import { CircleHelp } from "lucide-react";
import UserMenu from "./components/auth/UserMenu";
import DeviceConnectionDot from "./components/device/DeviceConnectionDot";
import ActiveWorkoutBanner from "./components/workout/ActiveWorkoutBanner";
import LiveSessionsBanner from "./components/social/LiveSessionsBanner";
import NotificationBell from "./components/social/NotificationBell";
import InFlightRecoveryBanner from "./components/telemetry/InFlightRecoveryBanner";
import CoachTelemetry from "./components/telemetry/CoachTelemetry";
import LandscapeOverlay from "./components/workout/LandscapeOverlay";
import IncomingLinkDialog from "./components/share/IncomingLinkDialog";
import NexusLinkDialog from "./components/integrations/NexusLinkDialog";
import logo from "./assets/images/khronos.svg";
import { PRIMARY_NAV_ITEMS } from "./config/navigation";
import usePhoneLandscape from "./hooks/usePhoneLandscape";
import useViewportWidth from "./hooks/useViewportWidth";
import { cn } from "./lib/utils";
import { onOpenBadges } from "./utils/badgeBus";
import { BadgesEarnedSheet } from "./components/social/Badges";

function App() {
  const { appState, view, setView, setSocialTab, setSettingsSheet, isImmersive } =
    useContext(AppStateContext);

  // A notification knows which Social tab its news lives on — a mate request
  // belongs on Mates, a like on the Feed — so send the view there rather than
  // dropping people on the feed to go looking. Shared credits and AI usage
  // live in Settings, so those open their sheet instead.
  const handleNotificationNavigate = useCallback(
    (target) => {
      if (target === "credits" || target === "usage") {
        setSettingsSheet(target);
        setView("settings");
        return;
      }
      // Your badges live on Activity (#204), and challenges under them.
      if (target === "badges" || target === "challenges") {
        setView("activity");
        return;
      }
      // A shared timer (#171) is listed under Khronos Timer in Settings.
      if (target === "timers") {
        setView("settings");
        return;
      }
      setSocialTab(target === "mates" ? "mates" : "feed");
      setView("social");
    },
    [setSocialTab, setSettingsSheet, setView],
  );
  const { setWorkoutPlan } = useContext(WorkoutContext);
  const live = useLiveSession();

  // #72: app-wide net for the Radix pointer-events lock surviving a layer that
  // unmounts mid-close, which leaves the whole UI unclickable until a refresh.
  useBodyPointerEventsGuard();

  // #75: offers a newly precached build, but never mid-session. Lives here
  // because it needs both the Coach's appState and the toast surface.
  useServiceWorkerUpdate();
  useTargetSuggestionNotice();
  // A rare effort in the session just saved.
  useRareEffortNotice();
  // Strap HR and bike power to the Khronos Timer, in and out of Live Mode.
  useTimerMetricsRelay();

  // Bridges the resume-toast (dispatched from SocialContext) into the
  // Workout/AppState contexts. Lives here because SocialProvider wraps
  // WorkoutProvider, so SocialContext can't reach the workout setters directly.
  useEffect(() => {
    const onResume = (e) => {
      const session = e.detail?.session;
      if (!session || !live?.rejoinLive) return;
      Promise.resolve(live.rejoinLive(session)).then(() => {
        if (session.plan_data) setWorkoutPlan(session.plan_data);
        setView("coach");
      });
    };
    window.addEventListener("live:resume_request", onResume);
    return () => window.removeEventListener("live:resume_request", onResume);
  }, [live, setWorkoutPlan, setView]);

  // The badge toast's "See them" (#201): to Activity, where the Badges card
  // opens its sheet (#204).
  useEffect(() => onOpenBadges(() => handleNotificationNavigate("badges")), [handleNotificationNavigate]);

  const activeView = view;
  // A phone on its side is about 370 px tall: one slim bar instead of a
  // header plus a tab bar (#227).
  const oneBar = usePhoneLandscape();
  // Below the desktop-tabs width the bar shows tab icons only.
  const viewportW = useViewportWidth();
  const iconTabs = oneBar && viewportW < 768;

  const viewRenderers = {
    programme: () => <ProgrammeView />,
    coach: () => <WorkoutView />,
    settings: () => (
      <div className="max-w-3xl mx-auto">
        <SettingsView />
      </div>
    ),
    profile: () => <ProfileView />,
    social: () => <SocialView />,
    activity: () => <ActivityView />,
    progress: () => <ProgressView />,
    session: () => <SessionView onNavigate={setView} />,
  };

  const renderView = viewRenderers[activeView] || viewRenderers.session;

  return (
    // reducedMotion="user" makes every Motion animation respect the OS-level
    // prefers-reduced-motion setting (transform/layout animations are skipped,
    // opacity/colour fades are kept). The CSS counterpart for non-Motion
    // animations lives in globals.css.
    <MotionConfig reducedMotion="user">
    <div className="min-h-screen bg-background text-foreground">
      {/* Recovery banner from any interrupted workout (telemetry only, no
          plan_data). Renders above the header so it's the first thing the
          user sees if a previous session was lost. */}
      {!isImmersive && <InFlightRecoveryBanner />}
      {/* Records the workout whichever tab is showing. */}
      <CoachTelemetry />
      {/* Top header — full-bleed (hidden during immersive Coach) */}
      {!isImmersive && (
        <header className="bg-black/30 backdrop-blur-md border-b border-border sticky top-0 z-50">
          <nav
            className={cn(
              "max-w-7xl mx-auto px-4 flex items-center justify-between",
              oneBar ? "h-11 gap-3" : "h-14 md:h-16",
            )}
          >
            <div className="flex items-center gap-3">
              <img
                src={logo}
                alt="KHRONOS logo"
                className={cn("w-auto select-none", oneBar ? "h-5" : "h-6 md:h-7")}
                draggable="false"
              />
              <h1 className={cn("font-black", oneBar ? "text-lg" : "text-xl md:text-2xl", iconTabs && "sr-only")}>KHRONOS AI</h1>
            </div>

            {/* Desktop tabs — hidden on mobile, except a phone on its side,
                where they replace the bottom tab bar (#227). */}
            <Tabs
              value={activeView}
              onValueChange={setView}
              className={cn("relative", oneBar ? "block" : "hidden md:block")}
            >
              <TabsList className={cn(oneBar && ["border-b-0", iconTabs ? "gap-1" : "gap-4"])}>
                {PRIMARY_NAV_ITEMS.map((item) => {
                  const Icon = item.icon;
                  return (
                    <TabsTrigger
                      key={item.value}
                      value={item.value}
                      data-tour={`nav-${item.value}`}
                      aria-label={item.label}
                      className={cn(oneBar && "gap-1.5 py-1.5", iconTabs && "px-3")}
                      disabled={
                        item.disableWhenPlanning && appState === "planning"
                      }
                    >
                      {oneBar && <Icon className={iconTabs ? "h-5 w-5" : "h-4 w-4"} />}
                      <span className={cn(iconTabs && "sr-only")}>{item.label}</span>
                    </TabsTrigger>
                  );
                })}
              </TabsList>
            </Tabs>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => openHelp()}
                aria-label="Help"
                data-tour="help"
                title="Help"
                className="grid h-9 w-9 place-items-center rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <CircleHelp className="h-5 w-5" />
              </button>
              <NotificationBell onNavigate={handleNotificationNavigate} />
              <DeviceConnectionDot />
              <UserMenu />
            </div>
          </nav>
        </header>
      )}

      {/* Live sessions banner — visible across all views */}
      {!isImmersive && (
        <LiveSessionsBanner onNavigateToLive={() => handleNotificationNavigate("feed")} />
      )}

      {/* Main content */}
      <main
        className={
          isImmersive
            ? "pt-0 pb-0"
            : oneBar
              ? "pt-2 pb-6"
              : "pt-4 sm:pt-6 pb-20 md:pb-12"
        }
      >
        <div
          className={
            isImmersive ? "" : "max-w-7xl mx-auto px-4"
          }
        >
          {renderView()}
        </div>
      </main>

      {/* Global active workout banner — visible on all views except Coach */}
      {!isImmersive && <ActiveWorkoutBanner />}

      {/* Mobile bottom tab bar (hidden during immersive Coach) */}
      {!isImmersive && !oneBar && (
        <nav className="md:hidden fixed bottom-0 inset-x-0 z-50 bg-black/60 backdrop-blur-lg border-t border-border">
          <div className="flex items-center justify-around h-14">
            {PRIMARY_NAV_ITEMS.map((item) => {
              const Icon = item.icon;
              const isActive = activeView === item.value;
              const isDisabled =
                item.disableWhenPlanning && appState === "planning";
              return (
                <button
                  key={item.value}
                  data-tour={`nav-${item.value}`}
                  onClick={() => !isDisabled && setView(item.value)}
                  disabled={isDisabled}
                  className={`flex flex-col items-center gap-0.5 px-3 py-1.5 transition-colors ${
                    isActive
                      ? "text-primary"
                      : isDisabled
                        ? "text-muted-foreground/40"
                        : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="text-[10px] font-medium">{item.label}</span>
                </button>
              );
            })}
          </div>
        </nav>
      )}

      {/* Landscape rotate-back overlay (only active during immersive Coach) */}
      <LandscapeOverlay />

      {/* First-launch walkthrough; also opened from the Session tab banner
          and Profile. Renders nothing while closed. */}
      <GettingStarted />
      <HelpCentre />
      <Tour />

      {/* Renders only when a share/invite code is waiting (captured from the
          URL at startup in index.js). Sits at app level because a link can be
          opened from any view, signed in or out. */}
      <IncomingLinkDialog />
      <NexusLinkDialog />
      {/* Badges a session just earned, after Save (#204). */}
      <BadgesEarnedSheet />
    </div>
    </MotionConfig>
  );
}

export default App;
